import { tidyKeyword, tagSubGroup } from "./keywords";
import { matchNicheFromKeywords, mostCommonSubGroup, UNSORTED_NICHE_NAME } from "./niches";
import {
  searchesPerSample, totalSearches, plannedSampleTimes, estimatedFinish,
  showMinuteWarning, reportName, overPostcodeLimit,
} from "./scanPlan";
import { pickRadiusPostcodes, combineLocations } from "../locations/radius";

// Everything worked out for one scan, used by the search page and the CSV
// upload alike: tidied keywords, the niche and sub-group, the postcodes
// searched (radius picks, then extras), search counts, sample times and the
// report name.
// keywords: raw text. centres: the business locations ([{ postcode, lat, lng, places }]).
// extras: extra postcodes on top of the picks. districts: postcode_districts.
// maxPostcodes: the account's postcode limit.
// nicheChoice: null = from the keywords; { niche } or { suggestion: { id, text } }.
export function planScan({
  keywords, nicheChoice = null, samples, spacing, centres, radius, shuffle = 0, removedPicks = [],
  extras = [], districts, maxPostcodes, niches, now,
}) {
  const { picks, gaps } = pickRadiusPostcodes({ districts, centres, radiusMiles: radius, shuffle });
  const { postcodes, added, skipped } = combineLocations({ picks, removed: removedPicks, extras });
  const places = [...new Set([...centres, ...postcodes].flatMap(p => p.places || []))];
  const standardForms = keywords.map(raw => tidyKeyword(raw, places) || raw.toLowerCase());

  const autoMatch = matchNicheFromKeywords(standardForms, niches);
  const unsorted = niches.find(n => n.name === UNSORTED_NICHE_NAME) || null;

  let niche = null;
  let suggestion = null;
  if (nicheChoice && nicheChoice.niche) niche = nicheChoice.niche;
  else if (nicheChoice && nicheChoice.suggestion) { niche = unsorted; suggestion = nicheChoice.suggestion; }
  else if (autoMatch) niche = autoMatch.niche;

  const keywordSubGroups = standardForms.map(form => (niche ? tagSubGroup(form, niche.sub_groups) : null));
  const subGroup = mostCommonSubGroup(keywordSubGroups);

  const tidied = keywords.map((raw, i) => ({
    raw, standard: standardForms[i], subGroupId: keywordSubGroups[i] ? keywordSubGroups[i].id : null,
  }));

  const perSample = searchesPerSample(keywords.length, postcodes.length);
  const codes = postcodes.map(p => p.postcode);
  const overLimit = overPostcodeLimit(postcodes.length, maxPostcodes);

  return {
    centres,
    postcodes,
    picks,
    added,
    skipped,
    gaps,
    maxPostcodes,
    overLimit,
    tidied,
    standardForms,
    niche,
    subGroup,
    suggestion,
    // Runs under Unsorted when no niche was found or suggested.
    runNiche: niche || unsorted,
    isAutoNiche: !nicheChoice && !!autoMatch,
    perSample,
    total: totalSearches(keywords.length, postcodes.length, samples),
    sampleTimes: plannedSampleTimes(now, samples, spacing),
    finish: estimatedFinish(now, samples, spacing, perSample),
    warning: showMinuteWarning(spacing, perSample),
    name: reportName(suggestion ? suggestion.text : (niche || unsorted || {}).name, codes, now),
    canRun: keywords.length > 0 && postcodes.length > 0 && !overLimit,
  };
}
